import { Controller } from '@hotwired/stimulus'

// Reusable TOC: headings crossing their scroll margin select the current link.
// At the bottom the final section wins. Native anchor links scroll normally;
// clicks and URL hashes never override the section selected by scroll position.
export default class extends Controller {
    connect() {
        this.links = new Map()
        for (const link of this.element.querySelectorAll('a[href^="#"]')) {
            const section = this.sectionForHash(link.hash)
            if (section) this.links.set(section, link)
        }
        this.scrollRoot = this.findScrollRoot()
        this.events = new AbortController()
        const options = { passive: true, signal: this.events.signal }
        const scrollTarget =
            this.scrollRoot === document.scrollingElement ? window : this.scrollRoot

        scrollTarget.addEventListener('scroll', this.scheduleUpdate, options)
        window.addEventListener('resize', this.scheduleUpdate, options)

        this.observer = new ResizeObserver(this.scheduleUpdate)
        this.observer.observe(this.scrollRoot)
        for (const section of this.links.keys()) this.observer.observe(section)
        this.markCurrent()
    }

    disconnect() {
        this.events?.abort()
        this.observer?.disconnect()
        cancelAnimationFrame(this.frame)
        this.frame = null
    }

    sectionForHash(hash) {
        try {
            return hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null
        } catch {
            return null
        }
    }

    findScrollRoot() {
        const first = this.links.keys().next().value
        for (let parent = first?.parentElement; parent; parent = parent.parentElement) {
            // Body overflow is normally propagated to the document viewport.
            if (parent === document.body || parent === document.documentElement) break
            if (/(auto|scroll|overlay)/.test(getComputedStyle(parent).overflowY)) return parent
        }
        return document.scrollingElement
    }

    scheduleUpdate = () => {
        if (this.frame != null) return
        this.frame = requestAnimationFrame(() => {
            this.frame = null
            this.markCurrent()
        })
    }

    markCurrent() {
        const sections = [...this.links.keys()].filter((section) => section.getClientRects().length)
        let current = sections[0] || null
        const root = this.scrollRoot
        const rootTop =
            root === document.scrollingElement
                ? 0
                : root.getBoundingClientRect().top + root.clientTop
        const atBottom =
            root.scrollHeight > root.clientHeight &&
            root.scrollHeight - root.clientHeight - root.scrollTop <= 2

        if (atBottom) {
            current = sections.at(-1) || null
        } else {
            for (const section of sections) {
                const offset = parseFloat(getComputedStyle(section).scrollMarginTop) || 0
                if (section.getBoundingClientRect().top <= rootTop + offset + 2) current = section
            }
        }

        for (const [section, link] of this.links) {
            const active = section === current
            link.classList.toggle('active', active)
            if (active) link.setAttribute('aria-current', 'location')
            else link.removeAttribute('aria-current')
        }
    }
}
